import { useState } from 'react';

import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@/ui';

import type { CardPayConversionState } from '@/store/api/cardPayConversionApi';
import type { FC, ReactNode } from 'react';

import {
  useGetCardPayConversionQuery,
  useLinkConversionStripeMutation,
  useRunConversionStepMutation,
} from '@/store/api/cardPayConversionApi';

const ERROR_MESSAGES: Record<string, string> = {
  customer_id_required: 'Enter a Stripe customer id starting with cus_.',
  customer_linked_to_another_user:
    'That Stripe customer is already linked to another member.',
  multiple_current_memberships:
    'This member belongs to more than one company. Ask engineering.',
  no_current_membership: 'This member has no current company membership.',
  not_domain_member:
    'This member is linked only through the contracted list, not the company domain. Ask engineering.',
  payment_not_confirmed:
    'Stripe has not confirmed a paid invoice yet. Check Stripe, or tick “send to paywall”.',
  stripe_not_linked: 'Link the Stripe customer first.',
};

const errorMessage = (error: unknown): string | null => {
  if (!error) return null;
  const code =
    typeof error === 'object' && 'data' in error
      ? (error as { data?: { error?: string } }).data?.error
      : undefined;
  return (
    (code && ERROR_MESSAGES[code]) ?? 'Something went wrong. Please try again.'
  );
};

const Step: FC<{ children?: ReactNode; done: boolean; title: string }> = ({
  children,
  done,
  title,
}) => (
  <div className="border-b border-cement-300 py-3 last:border-b-0">
    <Typography variant="subtitle2">
      {done ? '✓' : '○'} {title}
    </Typography>
    <div className="mt-2 space-y-2">{children}</div>
  </div>
);

const paymentSummary = (state: CardPayConversionState): string => {
  const check = state.paymentCheck;
  if (check) {
    return `Subscription ${check.subscriptionStatus}, latest invoice ${check.latestInvoiceStatus ?? 'none'}`;
  }
  return state.stripe?.status
    ? `Subscription ${state.stripe.status}`
    : 'Not checked';
};

const endDateSummary = ({ endDate }: CardPayConversionState): string => {
  if (!endDate.date) return 'Ongoing';
  const status = endDate.status ? ` (${endDate.status})` : '';
  return `Ends ${endDate.date}${status}. Clear it in HubSpot, then re-sync.`;
};

const confirmText = (companyName: string, sendToPaywall: boolean): string =>
  `${companyName} will stop paying for this member. They stay linked to ${companyName} and will pay by card${
    sendToPaywall ? ', starting with the paywall on their next visit' : ''
  }.`;

interface CardPayConversionProps {
  userId: string;
}

// Staff flow for moving a company-paid member to paying by card without ending their membership.
const CardPayConversionPanel: FC<CardPayConversionProps> = ({ userId }) => {
  // currentData is only ever this member's result, never a previous member's while a refetch is in flight.
  const { currentData: state } = useGetCardPayConversionQuery(userId);
  const [linkStripe, linkResult] = useLinkConversionStripeMutation();
  const [runStep, stepResult] = useRunConversionStepMutation();
  const [customerId, setCustomerId] = useState('');
  const [sendToPaywall, setSendToPaywall] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const membership = state?.membership;
  if (!state || !membership) return null;

  const { steps } = state;
  const paidCheckPassed = state.paymentCheck?.paid ?? false;
  const canConvert = !steps.cardPay && (paidCheckPassed || sendToPaywall);
  const error = errorMessage(stepResult.error ?? linkResult.error);
  const busy = linkResult.isLoading || stepResult.isLoading;

  const handleLinkStripe = () => {
    linkStripe({ customerId: customerId.trim(), userId });
  };
  const handleVerify = () => {
    runStep({ step: 'verify_payment', userId });
  };
  const handleOpenConfirm = () => setConfirming(true);
  const handleCloseConfirm = () => setConfirming(false);
  const handleConvert = () => {
    setConfirming(false);
    runStep({ sendToPaywall, step: 'convert', userId });
  };
  const handleResync = () => {
    runStep({ step: 'resync_end_date', userId });
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1">Convert to card pay</Typography>
        <Typography className="mb-2 block" variant="caption">
          {membership.companyName} · {membership.paymentStatus}
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}

        <Step done={steps.stripeLinked} title="1. Link Stripe">
          {state.stripe?.customerId && (
            <Typography variant="body2">{state.stripe.customerId}</Typography>
          )}
          <div className="flex gap-2">
            <TextField
              label="Stripe customer id"
              placeholder="cus_…"
              size="small"
              value={customerId}
              onChange={(event) => setCustomerId(event.target.value)}
            />
            <Button
              disabled={busy || !customerId.trim()}
              variant="outlined"
              onClick={handleLinkStripe}
            >
              Link
            </Button>
          </div>
        </Step>

        <Step done={paidCheckPassed} title="2. Confirm payment">
          <Typography variant="body2">{paymentSummary(state)}</Typography>
          <Button
            disabled={busy || !steps.stripeLinked}
            variant="outlined"
            onClick={handleVerify}
          >
            Check Stripe
          </Button>
        </Step>

        <Step done={steps.cardPay} title="3. Mark card pay">
          {!steps.cardPay && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendToPaywall}
                    onChange={(event) => setSendToPaywall(event.target.checked)}
                  />
                }
                label="Send to paywall without a confirmed payment"
              />
              <Button
                disabled={busy || !canConvert}
                variant="contained"
                onClick={handleOpenConfirm}
              >
                Convert
              </Button>
            </>
          )}
        </Step>

        <Step done={steps.endDateOngoing} title="4. End date → ongoing">
          <Typography variant="body2">{endDateSummary(state)}</Typography>
          {!steps.endDateOngoing && (
            <Button disabled={busy} variant="outlined" onClick={handleResync}>
              Re-sync from HubSpot
            </Button>
          )}
        </Step>
      </CardContent>

      <Dialog open={confirming} onClose={handleCloseConfirm}>
        <DialogTitle>Convert to card pay?</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmText(membership.companyName, sendToPaywall)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="gray" variant="outlined" onClick={handleCloseConfirm}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConvert}>
            Convert to card pay
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// Keyed by member so typed ids, the paywall choice and an open confirmation never carry over to another member.
const CardPayConversion: FC<CardPayConversionProps> = ({ userId }) => (
  <CardPayConversionPanel key={userId} userId={userId} />
);

export default CardPayConversion;
